import {
  createContext, useCallback, useContext, useMemo, useState,
  type ReactNode,
} from 'react'
import { periodsOfYear } from '../lib/format'

const STORAGE_KEY = 'escale.annee'

interface YearValue {
  /** L'annee choisie, ou null tant que rien n'est choisi. */
  year: number | null
  /** Les douze periodes de cette annee. */
  periods: string[]
  setYear: (year: number) => void
  /** Revient a l'ecran de choix de l'annee. */
  clearYear: () => void
}

const YearContext = createContext<YearValue | null>(null)

/**
 * L'ANNEE EST LE CADRE DE TOUTE L'APPLICATION.
 *
 * Apres la connexion, on choisit une annee, et tout ce qui s'affiche
 * ensuite s'y rapporte : loyers, depenses, pieces justificatives,
 * rapports. C'est le meme geste que de prendre un classeur d'annee sur
 * une etagere.
 *
 * Le choix est retenu dans le navigateur : on ne le refait pas a chaque
 * ouverture de l'application.
 *
 * Une annee est ici les douze periodes portant son millesime. Chaque
 * periode courant du 5 au 5, l'annee 2026 va donc du 05/01/2026 au
 * 05/01/2027.
 */
function readStored(): number | null {
  try {
    const brut = localStorage.getItem(STORAGE_KEY)
    if (!brut) return null
    const n = Number(brut)
    // Garde-fou : une valeur abimee ne doit pas bloquer l'application
    // sur une annee absurde.
    return Number.isInteger(n) && n >= 2000 && n <= 2100 ? n : null
  } catch {
    return null
  }
}

export function YearProvider({ children }: { children: ReactNode }) {
  const [year, setYearState] = useState<number | null>(readStored)

  const setYear = useCallback((next: number) => {
    setYearState(next)
    try {
      localStorage.setItem(STORAGE_KEY, String(next))
    } catch {
      // Navigation privee : le choix ne vaut que pour cette session.
    }
  }, [])

  const clearYear = useCallback(() => {
    setYearState(null)
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Rien a faire.
    }
  }, [])

  const value = useMemo<YearValue>(
    () => ({
      year,
      periods: year === null ? [] : periodsOfYear(year),
      setYear,
      clearYear,
    }),
    [year, setYear, clearYear],
  )

  return <YearContext.Provider value={value}>{children}</YearContext.Provider>
}

export function useYear(): YearValue {
  const value = useContext(YearContext)
  if (!value) throw new Error('useYear doit etre utilise dans un YearProvider')
  return value
}

/**
 * L'annee choisie, garantie non nulle.
 *
 * A utiliser dans les ecrans qui ne sont atteignables qu'apres le choix
 * d'une annee, ce qui evite de traiter partout un cas impossible.
 */
export function useSelectedYear(): { year: number; periods: string[] } {
  const { year, periods } = useYear()
  if (year === null) {
    throw new Error('Aucune annee choisie : cet ecran ne devrait pas etre affiche')
  }
  return { year, periods }
}
